import { Drawer, Image, Descriptions, List, Typography, Empty, Spin, Space, Button, message } from 'antd';
import { LinkOutlined, EditOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type MediaRow } from '../../api/client';
import { fmtTaipei } from '../article-list/status';
import { displayName } from './MediaCard';
import { useDrawerWidth } from '../../hooks/useIsMobile';

interface Props {
  item: MediaRow | null;
  onClose: () => void;
}

/** 圖片詳情：看大圖、尺寸、網址，以及「這張圖以前刊在哪幾篇」——找舊照片時最關鍵的線索。 */
export default function MediaDetailDrawer({ item, onClose }: Props) {
  const drawerWidth = useDrawerWidth(560);
  const usage = useQuery({
    queryKey: ['media-usage', item?.id],
    queryFn: () => api.mediaUsage(item!.id),
    enabled: !!item,
  });

  return (
    <Drawer title="圖片資訊" open={!!item} onClose={onClose} width={drawerWidth}>
      {item && (
        <>
          <Image src={item.url} alt={item.alt ?? ''} className="rounded" />

          {/* 網址放最上面：找到照片後最常做的事就是複製網址或開原圖 */}
          <Space.Compact className="mt-3 w-full">
            <Typography.Text
              className="flex-1 truncate rounded-l border border-r-0 border-gray-200 bg-gray-50 px-2 py-1 text-xs leading-6"
              title={item.url}
            >
              {item.url}
            </Typography.Text>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(item.url);
                message.success('已複製網址');
              }}
            >
              複製網址
            </Button>
            <Button icon={<LinkOutlined />} href={item.url} target="_blank" rel="noopener noreferrer">
              開原圖
            </Button>
          </Space.Compact>

          <Descriptions size="small" column={1} className="mt-4" bordered>
            <Descriptions.Item label="檔名">{displayName(item)}</Descriptions.Item>
            {item.alt && <Descriptions.Item label="圖說 / alt">{item.alt}</Descriptions.Item>}
            <Descriptions.Item label="尺寸">
              {item.width && item.height ? `${item.width} × ${item.height}` : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="上傳時間">{fmtTaipei(item.createdAt)}</Descriptions.Item>
            <Descriptions.Item label="最近刊出">
              {item.publishedAt ? fmtTaipei(item.publishedAt) : '尚未被文章使用'}
            </Descriptions.Item>
          </Descriptions>

          <div className="mt-4 mb-2 text-sm font-medium">用過這張圖的文章</div>
          {usage.isLoading ? (
            <Spin />
          ) : usage.data?.items.length ? (
            <List
              size="small"
              dataSource={usage.data.items}
              renderItem={(a) => (
                <List.Item>
                  {/* 路由是 /articles/:id/edit；少了 /edit 會掉進萬用路由被導回文章列表 */}
                  <Link to={`/articles/${a.id}/edit`} onClick={onClose} className="line-clamp-2">
                    <EditOutlined className="mr-1" />
                    {a.title}
                  </Link>
                  <span className="ml-2 shrink-0 text-xs text-gray-400">
                    {a.publishedAt ? fmtTaipei(a.publishedAt).slice(0, 10) : '—'}
                  </span>
                </List.Item>
              )}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="尚未被任何文章使用" />
          )}
        </>
      )}
    </Drawer>
  );
}
